import {
  type ChangeEvent,
  type FunctionComponent,
  useRef,
  useState,
} from 'react';

import { Input, ThemeProvider } from '@dr.pogodin/react-utils';

import { isValidEmail, isValidName } from 'utils';

import inputTheme from 'themes/Input/admin/with-inner-label-60px.scss';

function isValidEmailPair(emailA: string, emailB: string): boolean {
  return emailA === emailB;
}

export type InputStateT = {
  email: string;
  firstName: string;
  lastName: string;
  valid: boolean;
};

type InputKeysT = 'email' | 'emailB' | 'firstName' | 'lastName';

type PropsT = {
  setState: (state: InputStateT) => void;
  state: InputStateT;
};

/**
 * Renders the block with "First Name", "Last Name", "Email Address",
 * and "Repeat Email Address" input fields, arranged vertically. This block
 * is used on "Add New Admin" and "Add New Practitioner" pages. It encapsulates
 * necessary validation and error messaging.
 */
const NamesAndEmailsInputBlock: FunctionComponent<PropsT> = ({
  setState,
  state,
}) => {
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const emailRefA = useRef<HTMLInputElement>(null);
  const emailRefB = useRef<HTMLInputElement>(null);

  const [emailB, setEmailB] = useState('');

  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [emailErrorA, setEmailErrorA] = useState('');
  const [emailErrorB, setEmailErrorB] = useState('');

  const newHandler = (key: InputKeysT) => (
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    const next = { ...state };

    let nextEmailB: string;

    if (key === 'emailB') {
      nextEmailB = e.target.value;
      setEmailB(nextEmailB);
    } else {
      nextEmailB = emailB;
      next[key] = e.target.value;
    }

    const validFirstName = isValidName(next.firstName);
    if (validFirstName && firstNameError) setFirstNameError('');

    const validLastName = isValidName(next.lastName);
    if (validLastName && lastNameError) setLastNameError('');

    const validEmail = isValidEmail(next.email);
    if (validEmail && emailErrorA) setEmailErrorA('');

    const validEmailPair = isValidEmailPair(next.email, nextEmailB);
    if (validEmailPair && emailErrorB) setEmailErrorB('');

    next.valid = validFirstName && validLastName
      && validEmail && validEmailPair;

    setState(next);
  };

  return (
    <ThemeProvider themes={{ Input: inputTheme }}>
      <Input
        error={firstNameError}
        label="First Name"
        onBlur={() => {
          if (!isValidName(state.firstName)) {
            setFirstNameError('No first name provided');
          }
        }}
        onChange={newHandler('firstName')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && isValidName(state.firstName)) {
            lastNameRef.current?.focus();
          }
        }}
        ref={firstNameRef}
        value={state.firstName}
      />
      <Input
        error={lastNameError}
        label="Last Name"
        onBlur={() => {
          if (!isValidName(state.lastName)) {
            setLastNameError('No last name provided');
          }
        }}
        onChange={newHandler('lastName')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && isValidName(state.lastName)) {
            emailRefA.current?.focus();
          }
        }}
        ref={lastNameRef}
        value={state.lastName}
      />
      <Input
        error={emailErrorA}
        label="Email Address"
        onBlur={() => {
          if (!state.email) setEmailErrorA('No email provided');
          else if (!isValidEmail(state.email)) setEmailErrorA('Invalid email');

          if (emailB && !isValidEmailPair(state.email, emailB)) {
            setEmailErrorB('Email values do not match');
          }
        }}
        onChange={newHandler('email')}
        ref={emailRefA}
        value={state.email}
      />
      <Input
        error={emailErrorB}
        label="Repeat Email Address"
        onBlur={() => {
          if (!isValidEmailPair(state.email, emailB)) {
            setEmailErrorB('Email values do not match');
          }
        }}
        onChange={newHandler('emailB')}
        ref={emailRefB}
        value={emailB}
      />
    </ThemeProvider>
  );
};

export default NamesAndEmailsInputBlock;
